/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import InputAdornment from '@material-ui/core/InputAdornment';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {
  getUserUnit,
  isImperial
} from '@ion/core/src/units/helpers';
import {
  Unit
} from '@ion/core/src/units/units';
import {PrimaryButton} from '@ion/dls/src/components/buttons/PrimaryButton';
import {TertiaryButton} from '@ion/dls/src/components/buttons/TertiaryButton';
import Chip from '@ion/dls/src/components/chip/Chip';
import Slider from '@ion/dls/src/components/slider/Slider';
import TextField from '@ion/dls/src/components/textfield/TextField';
import {useMedia} from '@ion/dls/src/hooks/useMedia';
import {Marks} from '@ion/dls/src/maps/components/findnearby/constants';
import {getDistance} from '@ion/dls/src/maps/components/findnearby/helper';
import {dlsAlt, dlsColor, dlsTypography} from '@ion/dls/src/theme/theme';

export interface OnFindNearByProps {
  findNearbyChip: {label: string, isSelected: boolean}[];
  uom: Unit;
  distance: any;
  label: string | null;
  asset: any;
}

interface FindNearbyProps {
  asset: any;
  onFindButtonClick?: (props: OnFindNearByProps) => void;
  onCancel: () => void;
}

const useStyles = makeStyles((theme) => ({
  popup : {
    boxSizing       : 'border-box',
    backgroundColor : dlsColor('white')
  },
  root : {
    display        : 'flex',
    justifyContent : 'center',
    flexFlow       : 'column',
    padding        : '24px 24px 20px 24px'
  },
  rootDiv : {
    display        : 'flex',
    justifyContent : 'center',
    flexDirection  : 'column'
  },
  chips : {
    display  : 'inline-flex',
    flexFlow : 'flex-start',
    flexWrap : 'wrap',
    width    : '100%'
  },
  chipsAlt : {
    display  : 'inline-flex',
    flexFlow : 'flex-start',
    flexWrap : 'wrap',
    width    : '100%'
  },
  divStyle : {
    display        : 'flex',
    justifyContent : 'space-between'
  },
  mobDivStyle : {
    display        : 'inline-flex',
    flexFlow       : 'row',
    justifyContent : 'space-between',
    marginBottom   : '-22px'
  },
  distance : {
    ...dlsAlt('components', 'findnearby', 'distance'),
    color : dlsColor('grey2')
  },
  distanceUOM : {
    ...dlsAlt('components', 'findnearby', 'distanceUOM'),
    color   : dlsColor('grey4'),
    padding : '4px 0 !important'
  },
  textBox : {
    '& input' : {
      paddingLeft : '8px'
    },
    [theme.breakpoints.down('sm')] : {
      '& input' : {
        paddingLeft : '5px'
      }
    },
    '& p' : {
      ...dlsAlt('components', 'findnearby', 'distanceUOM'),
      color   : dlsColor('grey4'),
      padding : '4px 0 !important'
    },
    top : '-6px'
  },
  underline : {
    '&&&&' : {
      '&:hover' : {
        '&:before,&:after' : {
          borderColor       : `${dlsColor('caBlue')} !important`,
          borderBottomWidth : '1px !important',
          transition        : 'none'
        }
      }
    }
  },
  underlineError : {
    '&::after' : {
      borderColor : theme.palette.error.main
    }
  },
  slider : {
    padding : '16px 0px !important'
  },
  sliderAlt : {
    padding : '27px 0px !important'
  },
  findButton : {
    width          : '100%',
    display        : 'flex',
    justifyContent : 'space-between',
    maxHeight      : '40px',
    fontSize       : '16px'
  },
  mobFindButton : {
    marginTop : '20px'
  },
  textInput : {
    width : '86px'
  },
  mobTextInput : {
    width : '84px'
  },
  breadcrumbContainer : {
    padding : '0 24px 0 6px'
  },
  inactiveCrumb : {
    color  : dlsColor('grey4'),
    cursor : 'pointer'
  },
  activeCrumb : {
    color : dlsColor('grey3')
  },
  itemMobileHeader : {
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'space-between',
    padding        : '15px 10px',
    '& > *'        : {
      textTransform : 'uppercase',
      letterSpacing : '1px',
      fontWeight    : 'bold'
    },
    '& > *:last-child' : {
      textAlign : 'center !important'
    }
  },
  title : {
    ...dlsTypography('h4'),
    color      : dlsColor('grey1'),
    lineHeight : 0
  },
  mobileContainer : {
    backgroundColor : dlsColor('white'),
    paddingBottom   : '5px'
  },
  endAdornmentRoot : {
    '&> p' : {
      margin : '0 !important'
    }
  }
}));

export const FindNearByPopupContent = ({
  asset,
  onFindButtonClick,
  onCancel
}: FindNearbyProps) => {
  const uom                     = getUserUnit();
  const distanceUOM             = isImperial(uom) ? 'miles' : 'kilometers';
  const classes                 = useStyles();
  const {isDesktop}             = useMedia();
  const [distance, setDistance] = useState(isImperial(uom) ? 10 : 20);

  const label           = translate('tracking:breadcrumb.findNearby');
  const findNearbyLabel = translate('maps:findNearby.assets');

  const [findNearbyChip, updateFindNearbyChip] = useState([{
    label      : null,
    isSelected : true
  }]);

  useEffect(function() {
    if (findNearbyLabel !== 'findnearby.assets') {
      updateFindNearbyChip([{
        label      : findNearbyLabel,
        isSelected : findNearbyChip[0]?.isSelected
      }]);
    }
  }, [findNearbyLabel]);

  const handleChipClick = useCallback((e, label) => {
    const nextState = findNearbyChip.map((chip) => {
      if (chip.label === label) {
        chip.isSelected = !chip.isSelected;
      }
      return chip;
    });
    updateFindNearbyChip(nextState);
  }, [findNearbyChip, updateFindNearbyChip]);

  const handleButtonClick = useCallback(function(e) {
    e.stopPropagation();
    e.preventDefault();
    onFindButtonClick?.({
      findNearbyChip,
      uom,
      distance,
      label,
      asset
    });
  }, [findNearbyChip, uom, distance, asset, label]);

  function handleInputChange(e) {
    e.stopPropagation();
    e.preventDefault();
    const {target:{value}} = e;
    const marks = Marks[uom];
    const length = marks.length;
    if (length) {
      const distance = getDistance(marks, value);
      setDistance(distance);
    }
  };

  const onCancelClick = useCallback(function(e) {
    e.stopPropagation();
    e.preventDefault();
    onCancel();
  }, [onCancel]);

  const inputProperties = useMemo(() => ({
    classes : {
      root      : classes.distanceUOM,
      underline : classes.underline
    },
    endAdornment :
    <InputAdornment classes={{root: classes.endAdornmentRoot}} position='end'>
      {`${translate(`maps:findNearby.${distanceUOM}`)}`}
    </InputAdornment>
  }), []);

  const btnStyle = useMemo(() => ({
    maxHeight     : isDesktop ? '40px' : '60px',
    height        : isDesktop ? '30px' : 'inherit',
    width         : isDesktop ? '49%' : 'inherit',
    fontSize      : '12px',
    letterSpacing : '1px'
  }), [isDesktop]);

  const onSlide = function(event, value) {
    event.stopPropagation();
    event.preventDefault();
    setDistance(value as number);
  };
  return (
    <div className={isDesktop ? classes.popup : classes.mobileContainer}>
      <div className={isDesktop ? classes.root : classes.rootDiv}>
        <div className={isDesktop ? classes.divStyle : classes.mobDivStyle}>
          <>
            <span className={classes.distance}>
              {translate('maps:findNearby.distance')}
            </span>
          </>
          <div style={{display: 'inline-flex', justifyContent: 'flex-end'}}>
            <div className={isDesktop ? classes.textInput : classes.mobTextInput}>
              <TextField label=''
                className={classes.textBox}
                type='number'
                value={distance}
                onChange={handleInputChange}
                InputProps={inputProperties}/>
            </div>
          </div>
        </div>
        <div>
          <Slider step={0.1}
            marks={Marks[uom]}
            value={distance}
            max={isImperial(uom) ? 50 : 100}
            onSlide={onSlide}
            className={isDesktop ? classes.slider : classes.sliderAlt}
            input />
        </div>
        <div className={isDesktop ? classes.chips : classes.chipsAlt}>
          {findNearbyChip.map((chip, index) => {
            const cb = function(e) {
              handleChipClick(e, chip.label);
            };
            return (
              <div key={`${chip.label}-${index}`} style={{marginBottom: '10px'}}>
                <Chip label={chip.label}
                  variant='outlined'
                  isSelected={chip.isSelected}
                  onClick={cb}
                  onDelete={cb} />
              </div>
            );
          }
          )}
        </div>
        <div className={isDesktop ? classes.findButton : classes.mobFindButton}>
          <TertiaryButton style={{...btnStyle}}
            onClick={onCancelClick}>
            {translate('maps:geofences.cancel')}
          </TertiaryButton>
          <PrimaryButton style={{minWidth: 100, ...btnStyle}}
            disabled={distance === 0}
            onClick={handleButtonClick}
            padding={'0px'}>
            {translate('maps:findNearby.button')}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};

export default FindNearByPopupContent;
